import { logAmplitudeEvent } from 'amplitude/amplitude'
import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { parseQueryStringToDictionary } from 'util/misc'

const EmptyWrapper = ({ children }) => <>{children}</>

/**
 * Why we need this wrapper:
 * As part of allowing unauthenticated access to public Expo pages (https://github.com/open-solar/opensolar-todo/issues/14183),
 * we have switched from hashHistory to browserHistory (spa/app/src/App.js:218).
 *
 * To handle cases of hashes in existing urls, the useEffect function below is used to
 * remove these hashes and redirect users to the respective route.
 */
const HashRedirectWrapper = ({ children }) => {
  const history = useHistory()
  const location = useLocation()
  const hash = location.hash
  const pathname = location.pathname

  //Case handling
  //If the url has a hash and the pathname is /, we redirect to the hash value
  const handleHashRedirect = () => {
    if (pathname === '/') {
      logAmplitudeEvent('hash_url_caught', { hash })
      let redirect = hash.replace('#', '')

      //If the redirect does not start with a /, we add a / to the beginning
      if (!redirect.startsWith('/')) redirect = `/${redirect}`
      //Append search params to the redirect
      if (location.search) redirect += location.search

      //If the redirect is empty or is /, we redirect to /home
      if (!redirect || redirect === '/') history.replace('/home')

      history.replace(redirect)
    }
  }

  //Exception: If the url query params start with a #, we don't need to redirect (Segen Redirect)
  const hasHashQueryParams = () => {
    const urlParams = parseQueryStringToDictionary(location?.hash?.replace('#', ''))
    if (!urlParams) return false
    const urlParamNames = Object.keys(urlParams)
    // Segen Redirects include url params code, state, client_info after the hash.
    // They need to be consumed by the MSAL library before the URL is rewritten.
    // This may not be the most elegant solution but it's super-specific, which
    // reduces the risk of causing issues with other parts of the app.
    return (
      urlParamNames.includes('code') &&
      urlParamNames.includes('state') &&
      urlParamNames.includes('client_info') &&
      urlParamNames.length === 3
    )
  }

  useEffect(() => {
    if (!hash) return
    //Exception logic
    if (hasHashQueryParams()) return
    //Main wrapper logic
    handleHashRedirect()
  }, [pathname, hash])

  return <EmptyWrapper>{children}</EmptyWrapper>
}

export default HashRedirectWrapper
